import { useEffect, useMemo, useState } from "react";

import { Button, Icon_ShaprLogoRound, Modal, PlatformAPI, uuidv4 } from "@shapr3d/frontend-lib";
import { useSearchParams } from "react-router-dom";

import thumbnail from "../../assets/images/welcome_video_thumbnail.webp";
import { downloadPageUrl } from "../../config";
import { useHubspotSendDownloadLink } from "../../hooks/useHubspot";
import { useTracker } from "../../hooks/useTracker";
import useTranslation from "../../hooks/useTranslation";
import { ElementClickAction, ElementClickLocation } from "../../lib/TrackingAdapter";
import { SearchParam } from "../../types";
import { PageSource } from "../../types/tracking";

import style from "./WelcomeModal.module.css";

const isShapr3DPlatform = PlatformAPI.getInstance().isShaprSupportedPlatform();

export const WelcomeModal = () => {
  const tracker = useTracker();
  const { t, m } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialOpen =
    searchParams.get(SearchParam.Source) === PageSource.Signup &&
    searchParams.get(SearchParam.UTMSource) !== "app";

  const [isOpen, setIsOpen] = useState(initialOpen);

  const sendId = useMemo(() => uuidv4(), []);
  const {
    isSuccess: isSendDownloadLinkSuccess,
    isPending: isSendDownloadLinkPending,
    isError: isSendDownloadLinkError,
    mutate: sendDownloadLinkMutate,
  } = useHubspotSendDownloadLink(sendId);

  const sendDownloadLink = () => {
    sendDownloadLinkMutate(undefined);
  };

  useEffect(() => {
    if (isOpen) {
      tracker.trackWelcomeModalShown();
    }
  }, [isOpen, tracker]);

  const handleClose = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete(SearchParam.Source);
      return next;
    });
    setIsOpen(false);
  };

  return (
    <Modal
      fullScreenOnMobile={true}
      size="large"
      open={isOpen}
      onClose={handleClose}
      persistent={true}
      background={
        <>
          <video
            className={style.video}
            src="https://d29vl8nlkx98pf.cloudfront.net/Shapr3D_welcome_apple.mp4"
            poster={thumbnail}
            muted
            autoPlay
            playsInline
            loop
          />
          <div className={style.backdrop}></div>
        </>
      }
    >
      <div className={style.skipButton}>
        <Button
          variant="grayscale"
          onClick={() => {
            tracker.trackWelcomeModalClosed({
              reason: "skip",
            });

            handleClose();
          }}
        >
          {t(m.WelcomeModal.SkipForNow)}
        </Button>
      </div>
      <div className={style.container}>
        <Icon_ShaprLogoRound />

        {!isShapr3DPlatform && !isSendDownloadLinkSuccess && (
          <>
            <div className={style.title}>{t(m.WelcomeModal.ProCadTitle)}</div>
            <div className={style.descriptionContainer}>
              <div className={style.description}>{t(m.WelcomeModal.DesignedFor)}</div>
              <div className={style.description}>{t(m.WelcomeModal.NotAvailableOnPhones)}</div>
            </div>

            <Button
              className={style.buttonMargin}
              onClick={sendDownloadLink}
              disabled={isSendDownloadLinkPending}
            >
              {t(m.WelcomeModal.GetDownloadLink)}
            </Button>
            {isSendDownloadLinkError && (
              <div className={style.error}>{t(m.WelcomeModal.SendDownloadLinkError)}</div>
            )}
          </>
        )}

        {!isShapr3DPlatform && isSendDownloadLinkSuccess && (
          <>
            <div className={style.title}>{t(m.WelcomeModal.CheckInbox)}</div>
            <div className={style.descriptionContainer}>
              <div className={style.description}>{t(m.WelcomeModal.EmailSent)}</div>
            </div>
          </>
        )}
        {isShapr3DPlatform && (
          <>
            <div className={style.title}>{t(m.WelcomeModal.DownloadApp)}</div>

            <Button
              className={style.buttonMargin}
              onClick={() => {
                window.open(downloadPageUrl, "_self", "noopener,noreferrer");
                tracker.trackButtonClick({
                  action: ElementClickAction.GO_TO_DOWNLOAD,
                  location: ElementClickLocation.WELCOME_MODAL,
                });
                tracker.trackWelcomeModalClosed({
                  reason: "download",
                });

                handleClose();
              }}
            >
              {t(m.WelcomeModal.DownloadShapr3D)}
            </Button>
          </>
        )}
      </div>
    </Modal>
  );
};
